import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import HttpHelper from '../../utils/HttpHelper';
import { formatAppDate as formatDate } from '../../utils/dateFormat';

type Outcome = 'pass' | 'rejected';

interface Interview {
  id?: number;
  interviewId?: number;
  round: number;
  outcome: string;
  comments: string | null;
  conductedByDisplayName: string | null;
  conductedAt: string | null;
}

function getInterviewId(i: Interview): number {
  const v = (i.id ?? i.interviewId) as number | undefined;
  return typeof v === 'number' ? v : -1;
}

function normalizeInterviewList(response: any): Interview[] {
  if (!response) return [];
  if (Array.isArray(response)) return response as Interview[];
  if (Array.isArray(response.interviews)) return response.interviews as Interview[];
  return [];
}

function toDatetimeLocalValue(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

const Interview = () => {
  const { jobId, applicantId } = useParams<{ jobId: string; applicantId: string }>();

  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Delete confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [interviewToDelete, setInterviewToDelete] = useState<Interview | null>(null);

  const [round, setRound] = useState<number>(1);
  const [outcome, setOutcome] = useState<Outcome | ''>('');
  const [comments, setComments] = useState('');
  const [conductedAt, setConductedAt] = useState('');
  const [conductedByDisplayName, setConductedByDisplayName] = useState('');

  const hasParams = useMemo(() => Boolean(jobId && applicantId), [jobId, applicantId]);

  const maxRound = useMemo(() => {
    if (!interviews.length) return 0;
    return Math.max(...interviews.map(i => i.round ?? 0));
  }, [interviews]);

  const defaultNextRound = useMemo(() => {
    return maxRound + 1;
  }, [maxRound]);

  const resetForm = (nextRound: number) => {
    setEditingId(null);
    setRound(nextRound);
    setOutcome('');
    setComments('');
    setConductedAt('');
    setConductedByDisplayName('');
  };

  const fetchInterviews = async () => {
    if (!jobId || !applicantId) return;

    setLoadingList(true);
    setErrorMessage(null);

    try {
      const response = await HttpHelper.get(
        `/hr/jobs/${jobId}/applicants/${applicantId}/interviews`,
      );

      if (response?.errorMessage) {
        setErrorMessage(response.errorMessage);
        setInterviews([]);
        setShowForm(true);
        resetForm(1);
        return;
      }

      const list = normalizeInterviewList(response);
      list.sort((a, b) => (a.round ?? 0) - (b.round ?? 0));
      setInterviews(list);

      if (list.length === 0) {
        setShowForm(true);
        resetForm(1);
      } else {
        setShowForm(false);
        resetForm(Math.max(...list.map(i => i.round ?? 0)) + 1);
      }
    } catch (e: any) {
      setErrorMessage(e?.message ?? 'Failed to load interviews');
      setInterviews([]);
      setShowForm(true);
      resetForm(1);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!hasParams) return;
    fetchInterviews();
  }, [hasParams]);

  const validateForm = (): string | null => {
    if (!jobId || !applicantId) return 'Missing jobId/applicantId in URL';
    if (!round || round < 1) return 'Round must be 1 or higher';
    if (!outcome) return 'Pass/Fail? is required';
    if (!conductedByDisplayName.trim()) return 'Conducted By is required';
    if (conductedAt && Number.isNaN(new Date(conductedAt).getTime())) return 'Invalid date';
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    if (!jobId || !applicantId) return;

    setSaving(true);
    setErrorMessage(null);

    try {
      const payload = {
        round,
        outcome,
        comments: comments.trim() ? comments.trim() : null,
        conductedAt: conductedAt ? new Date(conductedAt).toISOString() : null,
        conductedByDisplayName: conductedByDisplayName.trim(),
      };

      let response: any;

      if (editingId) {
        response = await HttpHelper.put(
          `/hr/jobs/${jobId}/applicants/${applicantId}/interviews/${editingId}`,
          payload,
        );
      } else {
        response = await HttpHelper.post(
          `/hr/jobs/${jobId}/applicants/${applicantId}/interviews`,
          payload,
        );
      }

      if (response?.errorMessage) {
        setErrorMessage(response.errorMessage);
        return;
      }

      await fetchInterviews();

      if (editingId) {
        setShowForm(false);
      } else {
        setShowForm(false);
        resetForm(defaultNextRound + 1);
      }
    } catch (e: any) {
      setErrorMessage(e?.message ?? 'Failed to save interview');
    } finally {
      setSaving(false);
    }
  };

  const handleAddInterview = () => {
    setErrorMessage(null);
    resetForm(defaultNextRound);
    setShowForm(true);
  };

  const handleEdit = (i: Interview) => {
    const id = getInterviewId(i);
    if (id < 0) {
      setErrorMessage('Missing interview id from API response');
      return;
    }
    setErrorMessage(null);
    setEditingId(id);
    setRound(i.round ?? 1);
    setOutcome((i.outcome as Outcome) ?? '');
    setComments(i.comments ?? '');
    setConductedAt(toDatetimeLocalValue(i.conductedAt));
    setConductedByDisplayName(i.conductedByDisplayName ?? '');
    setShowForm(true);
  };

  const handleCancel = () => {
    setErrorMessage(null);
    setEditingId(null);
    if (interviews.length === 0) {
      resetForm(1);
      setShowForm(true);
      return;
    }
    resetForm(defaultNextRound);
    setShowForm(false);
  };

  const handleDelete = (i: Interview) => {
    const id = getInterviewId(i);
    if (id < 0) {
      setErrorMessage('Missing interview id from API response');
      return;
    }
    setInterviewToDelete(i);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!jobId || !applicantId || !interviewToDelete) return;
    const id = getInterviewId(interviewToDelete);
    setConfirmOpen(false);
    setSaving(true);
    setErrorMessage(null);
    try {
      await HttpHelper.delete(
        `/hr/jobs/${jobId}/applicants/${applicantId}/interviews/${id}`,
      );
      await fetchInterviews();
    } catch (e: any) {
      setErrorMessage(e?.message ?? 'Failed to delete interview');
    } finally {
      setSaving(false);
      setInterviewToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setInterviewToDelete(null);
  };

  if (!hasParams) {
    return (
      <Paper sx={{ padding: 1 }}>
        <Alert severity="error">Missing jobId/applicantId in the URL.</Alert>
      </Paper>
    );
  }

  return (
    <>
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Interview</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{' '}
          {interviewToDelete ? `Round ${interviewToDelete.round}` : 'this interview'}? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ padding: 1, width: '100%' }}>
        <Stack spacing={0.5}>
          <Typography variant="h5" align="center">
            Interview Tracker
          </Typography>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {loadingList ? (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading interviews...</Typography>
            </Stack>
          ) : (
            <>
              {interviews.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center">
                  No interviews yet. Create Round 1 below.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Round</TableCell>
                          <TableCell>Pass/Fail?</TableCell>
                          <TableCell>Comments</TableCell>
                          <TableCell>Conducted By</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {interviews.map(i => (
                          <TableRow key={getInterviewId(i) > 0 ? getInterviewId(i) : `${i.round}-${i.outcome}`}>
                            <TableCell>{i.round}</TableCell>
                            <TableCell>{i.outcome}</TableCell>
                            <TableCell sx={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>
                              {i.comments ?? '-'}
                            </TableCell>
                            <TableCell>{i.conductedByDisplayName ?? '-'}</TableCell>
                            <TableCell>{formatDate(i.conductedAt)}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Button variant="outlined" onClick={() => handleEdit(i)} disabled={saving}>
                                  Edit
                                </Button>
                                <Button variant="outlined" onClick={() => handleDelete(i)} disabled={saving}>
                                  Delete
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Button variant="contained" onClick={handleAddInterview} disabled={saving || showForm}>
                      Add Interview
                    </Button>
                    <Button variant="outlined" onClick={fetchInterviews} disabled={saving}>
                      Refresh
                    </Button>
                  </Stack>

                  <Divider />
                </>
              )}

              {showForm && (
                <Paper variant="outlined" sx={{ padding: 1, width: '100%' }}>
                  <Stack spacing={0.5}>
                    <Typography variant="h6" align="center">
                      {editingId ? 'Update Interview' : interviews.length === 0 ? 'Create Interview' : 'Add Interview'}
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
                        gap: 1,
                      }}
                    >
                      <TextField
                        label="Round"
                        type="number"
                        fullWidth
                        value={round}
                        onChange={e => setRound(Math.max(1, Number(e.target.value)))}
                        inputProps={{ min: 1 }}
                      />

                      <TextField
                        label="Pass/Fail?"
                        select
                        fullWidth
                        value={outcome}
                        onChange={e => setOutcome(e.target.value as Outcome)}
                      >
                        <MenuItem value="pass">Pass</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </TextField>

                      <TextField
                        label="Conducted By"
                        select
                        fullWidth
                        value={conductedByDisplayName}
                        onChange={e => setConductedByDisplayName(e.target.value)}
                      >
                        <MenuItem value="Sebastien Chabot">Sebastien Chabot</MenuItem>
                        <MenuItem value="Chun Pang (Paul) Wong">Chun Pang (Paul) Wong</MenuItem>
                      </TextField>

                      <TextField
                        label="Interview Date"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={conductedAt}
                        onChange={e => setConductedAt(e.target.value)}
                      />
                    </Box>

                    <TextField
                      label="Comments"
                      multiline
                      rows={3}
                      fullWidth
                      value={comments}
                      onChange={e => setComments(e.target.value)}
                    />

                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Interview'}
                      </Button>
                      <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </>
  );
};

export default Interview;